import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { inr, imgSrc } from "@/api";

const CartDrawer = () => {
  const { items, open, setOpen, setQty, remove, subtotal } = useCart();
  const navigate = useNavigate();

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            data-testid="cart-drawer-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[60] bg-maroon-ink/40 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 260 }}
            className="fixed top-0 right-0 bottom-0 z-[70] w-full max-w-md bg-ivory-card border-l border-gold/30 flex flex-col"
          >
            <div className="flex items-center justify-between px-5 sm:px-8 h-18 sm:h-20 border-b border-gold/25">
              <div>
                <p className="text-[10px] tracking-[0.4em] uppercase text-gold-dark">Your Selection</p>
                <h2 className="font-display text-2xl text-maroon-deep">The Wardrobe</h2>
              </div>
              <button
                data-testid="cart-drawer-close-button"
                onClick={() => setOpen(false)}
                className="p-2 text-maroon-deep hover:text-gold-dark transition-colors"
                aria-label="Close cart"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-5 sm:px-8 py-5 sm:py-6">
              {items.length === 0 ? (
                <div data-testid="cart-empty-state" className="h-full flex flex-col items-center justify-center text-center gap-4">
                  <p className="font-display text-2xl text-maroon-deep/70 italic">Your wardrobe awaits its first piece.</p>
                  <button
                    data-testid="cart-empty-shop-button"
                    onClick={() => { setOpen(false); navigate("/shop"); }}
                    className="text-xs tracking-[0.3em] uppercase text-gold-dark border-b border-gold pb-1 hover:text-maroon-deep transition-colors"
                  >
                    Explore the Collection
                  </button>
                </div>
              ) : (
                <ul className="space-y-6">
                  {items.map((item) => (
                    <motion.li
                      key={item.key}
                      layout
                      data-testid={`cart-item-${item.key}`}
                      className="flex gap-4 border-b border-maroon/10 pb-6"
                    >
                      <div className="w-16 h-20 sm:w-20 sm:h-24 overflow-hidden ring-1 ring-gold/30 shrink-0">
                        <img src={imgSrc(item.image)} alt={item.name} className="w-full h-full object-cover" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between gap-2">
                          <p className="font-display text-base sm:text-lg text-maroon-deep leading-tight">{item.name}</p>
                          <button
                            data-testid={`cart-item-remove-${item.key}`}
                            onClick={() => remove(item.key)}
                            className="text-clay hover:text-maroon transition-colors"
                            aria-label="Remove item"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="text-[10px] tracking-[0.25em] uppercase text-gold-dark mt-1">
                          {item.category_label} · Size {item.size}
                        </p>
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center border border-maroon/20">
                            <button
                              data-testid={`cart-item-decrease-${item.key}`}
                              onClick={() => setQty(item.key, item.qty - 1)}
                              className="p-1.5 text-maroon-deep hover:bg-ivory-deep transition-colors"
                            >
                              <Minus className="w-3.5 h-3.5" />
                            </button>
                            <span data-testid={`cart-item-qty-${item.key}`} className="w-8 text-center text-sm text-maroon-deep">{item.qty}</span>
                            <button
                              data-testid={`cart-item-increase-${item.key}`}
                              onClick={() => setQty(item.key, item.qty + 1)}
                              className="p-1.5 text-maroon-deep hover:bg-ivory-deep transition-colors"
                            >
                              <Plus className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          <p className="font-display text-lg text-maroon-deep">{inr(item.price * item.qty)}</p>
                        </div>
                      </div>
                    </motion.li>
                  ))}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-gold/25 px-5 sm:px-8 py-5 sm:py-6 space-y-4">
                <div className="flex justify-between items-baseline">
                  <span className="text-xs tracking-[0.3em] uppercase text-clay">Subtotal</span>
                  <span data-testid="cart-subtotal" className="font-display text-2xl text-maroon-deep">{inr(subtotal)}</span>
                </div>
                <p className="text-xs text-clay/80">Complimentary shipping across India · GST included</p>
                <button
                  data-testid="cart-drawer-checkout-button"
                  onClick={() => { setOpen(false); navigate("/checkout"); }}
                  className="w-full min-h-14 flex items-center justify-center gap-3 bg-maroon text-ivory py-4 text-[10px] sm:text-xs tracking-[0.3em] sm:tracking-[0.35em] uppercase hover:bg-maroon-hover transition-colors duration-300"
                >
                  Proceed to Checkout <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
